import React, { useState, useEffect } from 'react';
import styled from 'styled-components';

import { calculateTimeRemaining, formatTimeUnit } from 'src/utils/time-remaining';

type OutletCountdownTimerProps = {
  endDate?: string;
  message?: string;
};

export const OutletCountdownTimer = ({ endDate, message }: OutletCountdownTimerProps): JSX.Element | null => {
  const [timeRemaining, setTimeRemaining] = useState(() => calculateTimeRemaining(endDate));

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeRemaining(calculateTimeRemaining(endDate));
    }, 1000);

    return () => clearInterval(interval);
  }, [endDate]);

  if (!endDate) {
    return null;
  }

  if (timeRemaining.isExpired) {
    return null;
  }

  const defaultMessage = 'Time is ticking! Outlet closes in';
  const displayMessage = message ?? defaultMessage;

  return (
    <TimerBanner>
      <TimerContent>
        <TimerMessage>{displayMessage}</TimerMessage>
        <TimerUnits>
          <TimeText>
            {formatTimeUnit(timeRemaining.days)} <TimeLabel>DAYS</TimeLabel>
          </TimeText>
          <Separator>:</Separator>
          <TimeText>
            {formatTimeUnit(timeRemaining.hours)} <TimeLabel>HRS</TimeLabel>
          </TimeText>
          <Separator>:</Separator>
          <TimeText>
            {formatTimeUnit(timeRemaining.minutes)} <TimeLabel>MIN</TimeLabel>
          </TimeText>
          <Separator>:</Separator>
          <TimeText>
            {formatTimeUnit(timeRemaining.seconds)} <TimeLabel>SEC</TimeLabel>
          </TimeText>
        </TimerUnits>
      </TimerContent>
    </TimerBanner>
  );
};

const TimerBanner = styled.div`
  background-color: var(--dcm-collections-timer-bg);
  color: var(--dcm-collections-timer-text-color);
  padding: 16px 24px;
  display: flex;
  font-size: 14px;
  align-items: center;
  justify-content: center;
  border-radius: var(--dcm-radius-lg) var(--dcm-radius-lg) 0 0;

  @media screen and (max-width: 600px) {
    padding: 12px 16px;
  }
`;

const TimerContent = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  justify-content: center;

  @media screen and (max-width: 600px) {
    gap: 8px;
  }
`;

const TimerMessage = styled.div`
  font-weight: 600;
  line-height: 1.5;
  white-space: nowrap;

  @media screen and (max-width: 600px) {
    white-space: normal;
    text-align: center;
    width: 100%;
  }
`;

const TimerUnits = styled.div`
  display: flex;
  align-items: baseline;
  gap: 8px;

  @media screen and (max-width: 600px) {
    gap: 6px;
  }
`;

const TimeText = styled.span`
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.5px;
  white-space: nowrap;
`;

const TimeLabel = styled.span`
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
  letter-spacing: 0.5px;
`;

const Separator = styled.span`
  font-size: 13px;
  font-weight: 700;
  line-height: 1;
  opacity: 0.7;
  margin: 0 2px;

  @media screen and (max-width: 600px) {
    margin: 0 1px;
  }
`;
